import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCreateBrandingEditSession } from '~entities/studios/api/create-branding-edit-session';

import { KEYS } from '~shared/api/query-keys';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

const BRANDING_EDIT_SAVED = 'BRANDING_EDIT_SAVED';
const BRANDING_EDIT_CANCELLED = 'BRANDING_EDIT_CANCELLED';

function parentPortalOrigin(): string | null {
  const raw = import.meta.env.VITE_PARENT_PORTAL_URL;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    return new URL(raw.trim()).origin;
  } catch {
    return null;
  }
}

function buildEditPortalUrl(locationId: string, token: string): string | null {
  const rawBase = import.meta.env.VITE_PARENT_PORTAL_URL;
  if (typeof rawBase !== 'string' || !rawBase.trim()) return null;
  const base = rawBase.replace(/\/$/, '');
  const url = new URL(`${base}/login`);
  url.searchParams.set('studioId', locationId);
  url.searchParams.set('editBranding', '1');
  url.searchParams.set('brandingEditToken', token);
  return url.toString();
}

type EditParentPortalButtonProps = {
  locationId?: string;
};

export function EditParentPortalButton({
  locationId,
}: EditParentPortalButtonProps) {
  const queryClient = useQueryClient();
  const createSession = useCreateBrandingEditSession();
  const [open, setOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const allowedOrigin = useMemo(() => parentPortalOrigin(), []);

  const closeAndRefresh = useCallback(() => {
    setOpen(false);
    setIframeUrl(null);
    void queryClient.invalidateQueries({ queryKey: [KEYS.WAIT_LIST_STATUS] });
    void queryClient.invalidateQueries({ queryKey: [KEYS.STUDIO_DETAILS] });
    void queryClient.invalidateQueries({ queryKey: [KEYS.LOGIN_IMAGE] });
  }, [queryClient]);

  useEffect(() => {
    if (!open || !allowedOrigin) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return;
      const type =
        event.data && typeof event.data === 'object'
          ? (event.data as { type?: unknown }).type
          : undefined;
      if (type === BRANDING_EDIT_SAVED) {
        toast.success('Parent Portal branding saved');
        closeAndRefresh();
        return;
      }
      if (type === BRANDING_EDIT_CANCELLED) {
        closeAndRefresh();
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [allowedOrigin, closeAndRefresh, open]);

  const handleOpen = () => {
    if (!locationId) {
      toast.error('Studio location is not available yet.');
      return;
    }
    if (!import.meta.env.VITE_PARENT_PORTAL_URL) {
      toast.error('Parent Portal URL is not configured for this environment.');
      return;
    }

    const portalHost = (() => {
      try {
        return new URL(
          String(import.meta.env.VITE_PARENT_PORTAL_URL).trim(),
        ).hostname;
      } catch {
        return '';
      }
    })();
    if (
      import.meta.env.DEV &&
      portalHost &&
      portalHost !== 'localhost' &&
      portalHost !== '127.0.0.1'
    ) {
      toast.message(
        `Edit iframe is loading ${portalHost}. For local branding edit mode, set VITE_PARENT_PORTAL_URL to your local Parent Portal (e.g. http://localhost:5174) in .env.local and restart Studio.`,
      );
    }

    createSession.mutate(undefined, {
      onSuccess: (session) => {
        const url = buildEditPortalUrl(session.locationId, session.token);
        if (!url) {
          toast.error('Could not build Parent Portal edit URL.');
          return;
        }
        setIframeUrl(url);
        setOpen(true);
      },
      onError: () => {
        toast.error('Failed to start Parent Portal edit session.');
      },
    });
  };

  return (
    <>
      <Button
        type='button'
        className='gap-2'
        disabled={!locationId || createSession.isPending}
        onClick={handleOpen}
      >
        {createSession.isPending ? (
          <Loader2 className='size-4 animate-spin' />
        ) : (
          <Pencil className='size-4' />
        )}
        Edit Parent Portal
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            closeAndRefresh();
            return;
          }
          setOpen(next);
        }}
      >
        <DialogContent className='flex h-[95vh] w-[95vw] max-w-none flex-col gap-0 overflow-hidden p-0'>
          <DialogHeader className='shrink-0 border-b px-4 py-3 text-left'>
            <DialogTitle>Edit Parent Portal</DialogTitle>
            <DialogDescription>
              Preview Login, Home, and Find Classes. Theme colors live in the
              top bar and apply portal-wide. Page pencils edit that page only
              (login image, home promo, welcome copy).
            </DialogDescription>
          </DialogHeader>
          <div className='min-h-0 flex-1 bg-muted/20'>
            {iframeUrl ? (
              <iframe
                title='Edit Parent Portal'
                src={iframeUrl}
                className='h-full w-full border-0'
                allow='clipboard-write'
              />
            ) : (
              <div className='flex h-full items-center justify-center'>
                <Loader2 className='size-8 animate-spin' />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
